import { useEffect, useState } from "react";
import { Download, Box, Layers, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { DuckCanvasPanel } from "./DuckCanvasPanel";
import { PrintProfileCard } from "./PrintProfileCard";
import { ShareLinkRow } from "./ShareLinkRow";
import { PublicWallToggle } from "./PublicWallToggle";
import { QrBottomChoice, useQrDecision } from "./QrBottomChoice";
import { BaseColourPicker } from "./BaseColourPicker";
import { BeforeAfterCard } from "./BeforeAfterCard";
import { RenderStrip } from "./RenderStrip";
import { PrintSizePicker } from "./PrintSizePicker";
import { TurntableStrip } from "./TurntableStrip";
import { DuckStoryCard } from "./DuckStoryCard";
import { DuckActionsRow } from "./DuckActionsRow";
import { OrderPrintButton } from "./OrderPrintButton";
import { PrintReadyBadge } from "./PrintReadyBadge";
import { PaintAttentionCard } from "./PaintAttentionCard";
import { FidelityCard } from "./FidelityCard";
import { RepaintPanel } from "./RepaintPanel";
import { RenameTitle } from "./RenameTitle";
import api, { DEFAULT_HEIGHT, frontImage } from "../lib/api";
import { useDownload } from "../hooks/useDownload";
import { useRepaint } from "../hooks/useRepaint";
import { DownloadProgressModal } from "./DownloadProgressModal";
import { playQuack } from "../lib/quack";

const ReadyHeading = ({ creation, onUpdate }) => (
  <div>
    <div className="flex items-center gap-3 mb-3">
      <span className="inline-block rounded-full bg-accent/15 text-accent text-xs font-mono2 uppercase tracking-widest px-3 py-1">Ready to print</span>
      <span data-testid="duck-version-chip" className="inline-block rounded-full bg-muted text-muted-foreground text-xs font-mono2 px-3 py-1" title="Revision — bumped each time you regenerate, pick a style or branch">Rev {creation.version || 1}</span>
      {creation.engine && (
        <span
          data-testid="duck-engine-chip"
          className={`inline-block rounded-full text-xs font-mono2 px-3 py-1 border ${creation.engine === "v2" ? "border-accent/50 text-accent" : "border-border text-muted-foreground"}`}
          title={creation.engine === "v2" ? `Generator pipeline v2${creation.phase_f ? " · Phase F region paint" : ""}` : "Original generator pipeline"}
        >
          Engine: {creation.engine === "v2" ? `v2${creation.phase_f ? " · Phase F" : ""}` : "classic"}
        </span>
      )}
      <button
        type="button"
        data-testid="quack-btn"
        onClick={() => playQuack("manual", { force: true })}
        className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        title="Play the quack"
      >
        <Volume2 className="h-3.5 w-3.5" /> Quack
      </button>
    </div>
    <RenameTitle creation={creation} onUpdate={onUpdate} />
    <p className="text-muted-foreground mt-2">
      A real 3D mesh built from your approved render — outfit, hair and all. Export a Bambu Studio
      <span className="font-mono2 text-accent"> .3MF</span> pre-painted for multi-color printing, or a single-color
      <span className="font-mono2 text-accent"> .STL</span>.
    </p>
  </div>
);

export const Duck3DViewer = ({ creation, onNew, onBranch, busy, onUpdate }) => {
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const [qrOn, setQrOn] = useState(null);          // null = use the duck's saved choice / the ON-by-default
  const [remember, setRemember] = useState(false);
  const colors = creation.paint_opts?.colors || 8; // the slot count chosen before the sculpt (the duck is painted for it)
  const { downloadState, startDownload, cancelDownload, repaintAndDownload, downloadAnyway } = useDownload({ onUpdate });

  const qrCheck = useQrDecision({ creationId: creation.id, height, qr: qrOn });
  const qrChecked = qrOn === null ? qrCheck?.wanted !== false : qrOn;
  const download = (fmt) => startDownload(creation, fmt, height, colors, false, fmt === "3mf" ? qrChecked : null);

  // never silently skip: if the QR was wanted but cannot be printed, say so on the export itself
  const qrNote = qrCheck && qrChecked && qrCheck.state === "skipped" ? `No QR on this file — ${qrCheck.notice}` : "";
  // strong overhangs (a drape, a cape, a flared hem): the 3MF already turns tree supports on — say so on the export
  const supportNote = creation.overhang?.needs_supports ? creation.overhang.message : "";
  const exportNote = [qrNote, supportNote].filter(Boolean).join(" ");

  const chooseQr = async (next) => {
    setQrOn(next);
    if (remember) await saveQr(next);
  };

  const saveQr = async (value) => {
    try {
      const { data } = await api.post(`/creations/${creation.id}/print-options`, { qr_bottom: value });
      onUpdate?.(data);
      toast.success(value ? "Saved — this duck keeps its bottom QR." : "Saved — this duck prints with a plain bottom.");
    } catch {
      toast.error("Could not save that choice.");
    }
  };

  // Async repaint: the overlay + status live on the server (survive a reload); here we just drive the UI off it.
  const { running: repainting, error: repaintError, run: runRepaint, retry: retryRepaint } = useRepaint(creation, {
    onUpdate: (doc) => {
      onUpdate?.(doc);
      toast.success("Repainted — colours updated.");
    },
  });
  const repaint = (opts) => runRepaint(opts);

  return (
    <div className="grid lg:grid-cols-[1.25fr_1fr] gap-8 animate-pop">
      <DownloadProgressModal {...downloadState} note={exportNote} onCancel={cancelDownload} onRepaint={repaintAndDownload} onDownloadAnyway={downloadAnyway} />
      <div className="space-y-5 min-w-0">
        <DuckCanvasPanel creation={creation} height={height} repainting={repainting} />
        {repaintError && (
          <button
            type="button"
            data-testid="repaint-error-retry"
            onClick={() => retryRepaint()}
            className="w-full rounded-2xl border border-amber-500/60 bg-amber-500/10 px-4 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-500/20 transition-colors"
          >
            {repaintError} — tap to retry
          </button>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
          <Button data-testid="download-3mf-btn" disabled={repainting} onClick={() => download("3mf")} className="h-14 rounded-full text-base font-bold gap-2 duck-shadow whitespace-normal">
            <Download className="h-5 w-5 shrink-0" /> Bambu .3MF (multi-color) · {height} mm
          </Button>
          <Button data-testid="download-stl-btn" variant="secondary" disabled={repainting} onClick={() => download("stl")} className="h-14 rounded-full font-bold gap-2 px-5">
            <Box className="h-5 w-5" /> .STL
          </Button>
        </div>
        <ShareLinkRow id={creation.id} />
        <BeforeAfterCard creation={creation} onUpdate={onUpdate} />
        <RenderStrip creation={creation} />
        <PublicWallToggle creation={creation} onUpdate={onUpdate} />
        <QrBottomChoice
          decision={qrCheck}
          checked={qrChecked}
          onChange={chooseQr}
          onRemember={(v) => { setRemember(v); if (v) saveQr(qrChecked); }}
          remembered={remember}
          onSwitchHeight={setHeight}
          testid="qr-choice-download"
        />
        <BaseColourPicker creation={creation} onUpdate={onUpdate} />
        <TurntableStrip views={creation.turntable} busy={repainting} />
        {creation.palette?.length > 0 && (
          <RepaintPanel palette={creation.palette} paintOpts={creation.paint_opts} capacity={creation.slot_capacity} running={repainting} onRepaint={repaint}
            loadReread={() => api.get(`/creations/${creation.id}/reread-colours`).then((r) => r.data)} />
        )}
      </div>

      <div className="space-y-5 min-w-0">
        <DuckStoryCard creation={creation} onUpdate={onUpdate} />
        <OrderPrintButton id={creation.id} height={height} colors={colors} turntable={creation.turntable} render={frontImage(creation)} canBasket />
        <DuckActionsRow creation={creation} height={height} onNew={onNew} onBranch={onBranch} busy={busy} />
        <ReadyHeading creation={creation} onUpdate={onUpdate} />
        <PaintAttentionCard creation={creation} onUpdate={onUpdate} />
        <PrintReadyBadge creationId={creation.id} notice={creation.export_notice} />
        <FidelityCard fidelity={creation.fidelity} onRedo={onBranch} busy={busy} />
        <PrintSizePicker value={height} onChange={setHeight} />
        <PrintProfileCard creation={creation} height={height} qrCheck={qrCheck} />
        <QrBottomChoice
          decision={qrCheck}
          checked={qrChecked}
          onChange={chooseQr}
          onSwitchHeight={setHeight}
          testid="qr-choice-profile"
        />
        <Button data-testid="download-glb-btn" variant="outline" disabled={repainting} onClick={() => download("glb")} className="w-full min-h-12 h-auto py-3 rounded-full font-bold gap-2 whitespace-normal">
          <Layers className="h-5 w-5 shrink-0" /> Color Mixing version · textured .GLB for Texture-to-Color · {height} mm
        </Button>
        <p className="text-xs text-muted-foreground -mt-2" data-testid="glb-hint">
          Optional: in Bambu Studio 2.7.1+ Add → this .GLB → Texture-to-Color, then “Decompose Color” (CMYW/RYBW) to print with mixed filaments.
        </p>
      </div>
    </div>
  );
};
